import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { ExternalBankAccount } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { externalBankAccountsApi } from "@api";
import { queriesExternalBankAccounts } from "@query";
import { analyticsService } from "@services/analyticsService";

export const useExternalBankAccountReconnect = () => {
  const { openSnackbar } = useWSSnackbar();
  const [linkToken, setLinkToken] = useState<string>();

  const plaidLink = usePlaidLink({
    token: linkToken ?? null,
    onExit() {
      setLinkToken(undefined);
    },
    async onSuccess() {
      setLinkToken(undefined);
      await WSQueryCache.invalidateQueries(
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase
      );
      openSnackbar({
        type: "success",
        icon: {
          name: "check-circle"
        },
        message: "Account was successfully reconnected"
      });
    },
    onEvent(name: string, event: any) {
      analyticsService.track("Plaid Link Event Occurred", {
        eventName: name,
        ...event
      });

      // Plaid renders its iframe behind app modals unless bumped; mirror the
      // z-index workaround from the v3 link hook.
      // https://github.com/plaid/react-plaid-link/issues/110
      switch (name) {
        case "OPEN": {
          window.document
            .querySelectorAll('[id^="plaid-link-iframe"]')
            .forEach(
              el => ((el as HTMLIFrameElement).style.zIndex = "2147483647")
            );
          break;
        }
        case "EXIT": {
          window.document
            .querySelectorAll('[id^="plaid-link-iframe"]')
            .forEach(el => ((el as HTMLIFrameElement).style.zIndex = "-999"));
          document.body.style.overflow = "";
          break;
        }
        default: {
        }
      }
    }
  });

  useEffect(() => {
    if (linkToken && plaidLink.ready) {
      plaidLink.open();
    }
  }, [linkToken, plaidLink]);

  return useCallback(
    async (account: ExternalBankAccount) => {
      try {
        const token =
          await externalBankAccountsApi.reconnectExternalBankAccount({
            bankAccountId: account.id,
            bankAccountReconnectRequest: {}
          });
        setLinkToken(token.linkToken);
      } catch (error) {
        console.error("Failed to start account reconnect", error);
        openSnackbar({
          type: "error",
          message: "Failed to start reconnect. Please try again."
        });
      }
    },
    [openSnackbar]
  );
};
